// src/Views/ForgotPassword/ForgotPassword.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../firebase';
import './ForgotPassword.css';

const ForgotPassword = () => {
    const navigate = useNavigate();

    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            // Firebase handles the logic. If the email doesn't exist, 
            // modern security practices often imply we shouldn't reveal that, 
            // but Firebase will throw 'auth/user-not-found' if you want to catch it.
            await sendPasswordResetEmail(auth, email);
            setIsSubmitted(true);
        } catch (err) {
            console.error('Reset password error', err);
            let msg = 'Failed to send reset email.';
            
            if (err.code === 'auth/invalid-email') {
                msg = 'Please enter a valid email address.';
            } else if (err.code === 'auth/user-not-found') {
                // Optional: You can choose to show "Email sent" even if not found for security
                // But for user friendliness we often say:
                msg = 'No account found with this email.';
            }
            
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="forgot">
            {/* Atmosphere */}
            <div className="forgot__bg-glow forgot__bg-glow--one" />
            <div className="forgot__bg-glow forgot__bg-glow--two" />
            <div className="forgot__bg-grid" />

            <div className="forgot__container">
                <div className="forgot__card">
                    
                    {/* BACK BUTTON */}
                    <button 
                        className="forgot__back-btn" 
                        onClick={() => navigate('/signin')}
                    >
                        <Icon icon="solar:arrow-left-linear" />
                        <span>Back to Sign In</span>
                    </button>

                    {isSubmitted ? (
                        /* SUCCESS STATE */
                        <div className="forgot__success">
                            <div className="forgot__success-icon-wrap">
                                <Icon icon="solar:letter-opened-linear" className="forgot__success-icon" />
                            </div>

                            <h1 className="forgot__title">Check your email</h1>
                            <p className="forgot__subtitle">
                                We've sent password reset instructions to <strong className="forgot__highlight">{email}</strong>.
                            </p>

                            <div className="forgot__spam-box">
                                <Icon icon="solar:bell-bing-linear" className="forgot__spam-icon" />
                                <div className="forgot__spam-text">
                                    <span className="forgot__spam-title">Don't see it?</span>
                                    <span>Check your <strong>Spam</strong> folder. It may take a few minutes to arrive.</span>
                                </div>
                            </div>

                            <button 
                                type="button" 
                                className="forgot__btn forgot__btn--ghost"
                                onClick={() => navigate('/signin')}
                            >
                                <span>Return to Login</span>
                            </button>
                            
                            <button 
                                type="button" 
                                className="forgot__resend-link"
                                onClick={() => setIsSubmitted(false)}
                            >
                                Try a different email
                            </button>
                        </div>
                    ) : (
                        /* FORM STATE */
                        <form className="forgot__form" onSubmit={handleSubmit}>
                            <div className="forgot__header">
                                <div className="forgot__logo-pill">
                                    <Icon icon="solar:key-minimalistic-linear" />
                                    <span>Password Recovery</span>
                                </div>
                                <h1 className="forgot__title">Forgot Password?</h1>
                                <p className="forgot__subtitle">
                                    Enter the email associated with your AiPredict account and we'll send you a reset link.
                                </p>
                            </div>

                            <div className="forgot__field-group">
                                <label className="forgot__label">Email address</label>
                                <div className="forgot__input-wrap">
                                    <Icon icon="solar:letter-linear" className="forgot__input-icon" />
                                    <input
                                        type="email"
                                        className="forgot__input"
                                        placeholder="name@example.com"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                </div>
                            </div>

                            {error && (
                                <div className="forgot__error">
                                    <Icon icon="solar:danger-triangle-linear" />
                                    <span>{error}</span>
                                </div>
                            )}

                            <button 
                                type="submit" 
                                className="forgot__btn forgot__btn--primary" 
                                disabled={loading}
                            >
                                {loading ? (
                                    <span>Sending link...</span>
                                ) : (
                                    <>
                                        <span>Send Reset Link</span>
                                        <Icon icon="solar:plain-2-linear" />
                                    </>
                                )}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
};

export default ForgotPassword;